<template>
  <div class="p-6">
    <!-- Breadcrumb -->
    <div v-if="breadcrumb.length > 1" class="flex items-center gap-1 text-xs text-gray-400 mb-2 flex-wrap">
      <template v-for="(crumb, i) in breadcrumb" :key="crumb.id">
        <button
          v-if="i < breadcrumb.length - 1"
          class="hover:text-primary-500 truncate max-w-[120px]"
          @click="$emit('select-item', crumb)"
        >
          {{ crumb.title }}
        </button>
        <span v-else class="text-gray-600 font-medium truncate max-w-[160px]">{{ crumb.title }}</span>
        <FontAwesomeIcon v-if="i < breadcrumb.length - 1" :icon="['fas', 'chevron-right']" class="text-[8px] text-gray-300" />
      </template>
    </div>

    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-lg font-semibold text-gray-900">{{ requirement.title }}</h2>
      <div class="flex items-center gap-2">
        <span
          v-if="saving"
          class="text-[10px] text-gray-400 flex items-center gap-1"
        >
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-[10px]" />
          Saving
        </span>
        <button
          v-if="canAddChild"
          class="text-xs text-gray-400 hover:text-primary-500 flex items-center gap-1"
          title="Add child"
          @click="startAddChild"
        >
          <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[10px]" />
          Child
        </button>
        <button
          class="text-gray-400 hover:text-gray-600"
          @click="$emit('close')"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" />
        </button>
      </div>
    </div>

    <!-- Editable Fields -->
    <div class="space-y-4">
      <!-- Title -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Title</label>
        <input
          v-model="form.title"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          @blur="save"
        />
      </div>

      <!-- Type -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Type</label>
        <select
          v-model="form.requirement_type"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          @change="save"
        >
          <option
            v-for="t in requirementTypes"
            :key="t.id"
            :value="t.id"
          >
            {{ t.name }}
          </option>
        </select>
      </div>

      <!-- Tenant -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Tenant</label>
        <select
          v-model="form.tenant"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          @change="save"
        >
          <option
            v-for="t in tenantOptions"
            :key="t.id"
            :value="t.id"
          >
            {{ t.name }}
          </option>
        </select>
      </div>

      <!-- Status + Priority side by side -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            v-model="form.status"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            @change="save"
          >
            <option value="draft">Draft</option>
            <option value="approved">Approved</option>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
            <option value="open">Open</option>
            <option value="waiting">Waiting</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Priority</label>
          <select
            v-model="form.priority"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            @change="save"
          >
            <option value="p0">P0</option>
            <option value="p1">P1</option>
            <option value="p2">P2</option>
            <option value="p3">P3</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>

      <!-- Owner -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Owner</label>
        <select
          v-model="form.owner"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          @change="save"
        >
          <option :value="null">Unassigned</option>
          <option
            v-for="u in users"
            :key="u.id"
            :value="u.id"
          >
            {{ u.full_name }}
          </option>
        </select>
      </div>

      <!-- Requester -->
      <div v-if="requirement.requester_name">
        <label class="block text-xs font-medium text-gray-500 mb-1">Requester</label>
        <div class="text-sm text-gray-700 px-3 py-2">{{ requirement.requester_name }}</div>
      </div>

      <!-- Parent -->
      <div v-if="canChangeParent">
        <label class="block text-xs font-medium text-gray-500 mb-1">Parent</label>
        <Support_SearchableSelect
          :model-value="form.parent"
          :options="parentOptions"
          :exclude-id="requirement.id"
          :disabled="saving"
          placeholder="None (top-level)"
          @update:model-value="onParentChange"
        />
      </div>

      <!-- Due Date -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Due Date</label>
        <input
          v-model="form.due_date"
          type="date"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          @change="save"
        />
      </div>

      <!-- Customer Facing -->
      <div>
        <div class="flex items-center gap-2">
          <input
            id="customer-facing-toggle"
            v-model="form.customer_facing"
            type="checkbox"
            class="h-4 w-4 rounded border-gray-300 text-primary accent-primary"
            @change="save"
          />
          <label for="customer-facing-toggle" class="text-xs font-medium text-gray-500">
            Customer Facing
          </label>
        </div>
        <p class="text-[11px] text-gray-400 mt-1 ml-6">
          Visible to customers in the Service Desk
        </p>
      </div>

      <!-- Tags -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Tags</label>
        <Support_TagEditor
          :model-value="form.tags"
          @update:model-value="onTagsChange"
        />
      </div>

      <!-- Description -->
      <div>
        <label class="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
          Description
          <span v-if="form.customer_facing !== false" class="inline-flex items-center gap-1 text-emerald-600 font-normal">
            <FontAwesomeIcon :icon="['fas', 'eye']" class="text-[10px]" />
            Visible to the customer
          </span>
        </label>
        <textarea
          v-model="form.description"
          rows="2"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none overflow-y-auto max-h-64"
          @input="autoResize"
          @blur="save"
        />
      </div>

      <!-- Internal notes (staff only -- never shown to customers) -->
      <div>
        <label class="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
          <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[10px] text-amber-600" />
          Internal notes
          <span class="text-amber-600 font-normal">Only your team can see this — never shown to customers</span>
        </label>
        <textarea
          v-model="form.internal_notes"
          rows="2"
          placeholder="Add an internal note..."
          class="w-full border border-amber-200 bg-amber-50 rounded px-3 py-2 text-sm resize-none overflow-y-auto max-h-48 focus:outline-none focus:ring-1 focus:ring-amber-400"
          @input="autoResize"
          @blur="save"
        />
      </div>

      <!-- Attachments -- Work and Service Desk use the same RequirementMedia
           source. Older items still fall back to metadata until migrated. -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Attachments</label>
        <RequirementAttachmentList
          v-if="requirementMediaFiles.length"
          :files="requirementMediaFiles"
          :use-media-endpoint="true"
          :removable="canDeleteAttachments"
          class="mb-2"
          @remove="removeAttachment"
        />
        <RequirementAttachmentList
          v-else-if="legacyAttachments.length"
          :files="legacyAttachments"
          class="mb-2"
        />
        <RequirementAttachmentUpload
          ref="attachUpload"
          :requirement-id="requirement.id"
          :surface="true"
          :drop-label="`to ticket #${requirement.id}`"
          @uploaded="onAttachmentUploaded"
        />
      </div>

      <!-- Children -->
      <div v-if="canAddChild">
        <div class="flex items-center justify-between mb-1.5">
          <label class="block text-xs font-medium text-gray-500">
            Children{{ childItems.length ? ` (${childItems.length})` : '' }}
          </label>
          <button
            v-if="!addingChild"
            class="text-[10px] text-gray-400 hover:text-primary-500 flex items-center gap-0.5"
            @click="startAddChild"
          >
            <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[8px]" />
            Add
          </button>
        </div>
        <div class="space-y-1">
          <button
            v-for="child in childItems"
            :key="child.id"
            class="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-gray-50 group transition-colors"
            @click="$emit('select-item', child)"
          >
            <span
              class="text-[10px] font-medium px-1 py-0.5 rounded shrink-0"
              :class="childTypeBadgeClass(child.requirement_type_name)"
            >
              {{ child.requirement_type_name }}
            </span>
            <span class="text-sm text-gray-700 truncate flex-1">{{ child.title }}</span>
            <span
              class="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
              :class="childStatusClass(child.status)"
            >
              {{ childStatusLabel(child.status) }}
            </span>
          </button>
          <!-- Inline add child -->
          <div v-if="addingChild" class="flex items-center gap-2 px-2.5 py-1.5">
            <select
              v-model="newChildTypeId"
              class="text-[10px] font-medium border border-gray-200 rounded px-1 py-0.5 bg-white"
            >
              <option v-for="t in allowedChildTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
            <input
              ref="childTitleInput"
              v-model="newChildTitle"
              class="flex-1 text-sm border border-gray-200 rounded px-2 py-1 outline-none focus:border-primary-300"
              :placeholder="'Add ' + childTypeLabel + '...'"
              @keydown.enter="submitChild"
              @keydown.escape="addingChild = false"
            />
            <button
              class="text-xs text-primary-500 hover:text-primary-700 disabled:opacity-40"
              :disabled="!newChildTitle.trim()"
              @click="submitChild"
            >
              <FontAwesomeIcon :icon="['fas', 'check']" />
            </button>
            <button
              class="text-xs text-gray-400 hover:text-gray-600"
              @click="addingChild = false"
            >
              <FontAwesomeIcon :icon="['fas', 'xmark']" />
            </button>
          </div>
        </div>
      </div>

      <!-- Comments -->
      <div class="pt-4 border-t border-gray-200">
        <label class="block text-xs font-medium text-gray-500 mb-2">Comments</label>
        <RequirementCommentThread
          v-if="requirement.id"
          :requirement-id="requirement.id"
          @comment-count="onCommentCount"
        />
      </div>

      <!-- Activity -->
      <div class="pt-4 border-t border-gray-200">
        <label class="block text-xs font-medium text-gray-500 mb-2">Activity</label>
        <div v-if="activities.length" class="space-y-3">
          <div
            v-for="a in activities"
            :key="a.id"
            class="flex gap-2 text-sm"
          >
            <div class="text-gray-400 mt-0.5">
              <FontAwesomeIcon :icon="activityIcon(a.activity_type)" class="w-3.5" />
            </div>
            <div>
              <span class="text-gray-700" v-html="formatActivity(a)"></span>
              <div class="text-xs text-gray-400 mt-0.5">{{ formatRelativeTime(a.created_at) }}</div>
            </div>
          </div>
        </div>
        <div v-else class="text-xs text-gray-400">No activity yet</div>
      </div>

      <!-- Meta -->
      <div class="pt-4 border-t border-gray-200 text-xs text-gray-400 space-y-1">
        <div>Created: {{ formatDate(requirement.created_at) }}</div>
        <div>Updated: {{ formatDate(requirement.updated_at) }}</div>
      </div>

      <!-- Delete -->
      <div class="pt-4">
        <button
          v-if="!showDeleteConfirm"
          class="text-sm text-red-500 hover:text-red-700"
          @click="showDeleteConfirm = true"
        >
          Delete
        </button>
        <!-- Leaf item: simple confirm -->
        <div v-else-if="!childItems.length" class="flex items-center gap-2">
          <span class="text-sm text-gray-600">Delete this item?</span>
          <button
            class="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
            @click="deleteWithChildren"
          >
            Delete
          </button>
          <button
            class="text-xs text-gray-400 hover:text-gray-600"
            @click="showDeleteConfirm = false"
          >
            Cancel
          </button>
        </div>
        <!-- Has children: show options -->
        <div v-else class="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
          <div class="text-xs text-red-700">
            Has {{ descendantCount }} {{ descendantCount === 1 ? 'descendant' : 'descendants' }}
          </div>
          <div class="flex flex-col gap-1">
            <button
              class="text-xs text-left px-2 py-1.5 rounded border border-red-200 text-gray-700 hover:bg-red-100"
              @click="deleteAndPromoteChildren"
            >
              <FontAwesomeIcon :icon="['fas', 'arrow-up']" class="mr-1 text-gray-400" />
              Move children up, then delete
            </button>
            <button
              class="text-xs text-left px-2 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
              @click="deleteWithChildren"
            >
              <FontAwesomeIcon :icon="['fas', 'trash']" class="mr-1" />
              Delete everything
            </button>
            <button
              class="text-xs text-center text-gray-400 hover:text-gray-600 py-0.5"
              @click="showDeleteConfirm = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Support_SearchableSelect from './Support_SearchableSelect.vue';
import Support_TagEditor from './Support_TagEditor.vue';
import RequirementCommentThread from './RequirementCommentThread.vue';
import RequirementAttachmentList from './RequirementAttachmentList.vue';
import RequirementAttachmentUpload from './RequirementAttachmentUpload.vue';
import { useToast } from 'vue-toastification';
import {
  VALID_PARENT_TYPES,
  CHILD_TYPES_FOR_PARENT,
  TYPE_CLASSES,
  STATUS_CLASSES,
  STATUS_LABELS,
  getDescendantIds,
} from './support_constants.js';

export default {
  name: 'Support_Detail',
  components: {
    FontAwesomeIcon,
    Support_SearchableSelect,
    RequirementCommentThread,
    Support_TagEditor,
    RequirementAttachmentList,
    RequirementAttachmentUpload,
  },
  props: {
    requirement: { type: Object, required: true },
    requirementTypes: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    tenantOptions: { type: Array, default: () => [] },
    allItems: { type: Array, default: () => [] },
  },
  emits: ['close', 'updated', 'deleted', 'comment-count', 'select-item'],
  data() {
    return {
      form: { ...this.requirement, tags: this.requirement.tags || [] },
      requirementMediaFiles: [],
      activities: [],
      addingChild: false,
      newChildTitle: '',
      newChildTypeId: null,
      showDeleteConfirm: false,
      saving: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    toast() {
      return useToast();
    },
    legacyAttachments() {
      return this.form.metadata?.attachments || [];
    },
    // This matches the deletion permission enforced by RequirementMedia's API
    // and the Service Desk detail drawer.
    canDeleteAttachments() {
      return (
        this.current_user?.is_superuser ||
        this.$store.getters['tenant/isTenantAdmin'] ||
        this.$store.getters['tenant/isSecurityUser']
      );
    },
    commentTypeId() {
      const t = this.requirementTypes.find(t => t.name === 'Comment');
      return t?.id || null;
    },
    parentOptions() {
      const commentTypeId = this.commentTypeId;
      const myType = this.requirement.requirement_type_name;
      const validParents = VALID_PARENT_TYPES[myType];
      const descendants = getDescendantIds([this.requirement.id], this.allItems);
      return this.allItems.filter((item) => {
        if (descendants.has(item.id)) return false;
        if (item.requirement_type === commentTypeId) return false;
        if (validParents && !validParents.includes(item.requirement_type_name)) return false;
        return true;
      });
    },
    breadcrumb() {
      const chain = [];
      let current = this.requirement;
      const byId = {};
      for (const item of this.allItems) byId[item.id] = item;
      while (current) {
        chain.unshift(current);
        current = current.parent ? byId[current.parent] : null;
      }
      return chain;
    },
    childItems() {
      const commentTypeId = this.commentTypeId;
      return this.allItems.filter(
        (item) => item.parent === this.requirement.id && item.requirement_type !== commentTypeId,
      );
    },
    // The merged list has no tab to ask, so both sections follow the item's own
    // type. Bug / Support Ticket now take an Epic or Feature parent (that is
    // already Irene's practice) but remain leaves, so Parent shows and
    // Children does not.
    canAddChild() {
      return !!CHILD_TYPES_FOR_PARENT[this.requirement.requirement_type_name];
    },
    canChangeParent() {
      return !!VALID_PARENT_TYPES[this.requirement.requirement_type_name];
    },
    allowedChildTypes() {
      const parentType = this.requirement.requirement_type_name;
      const allowed = CHILD_TYPES_FOR_PARENT[parentType];
      if (!allowed) return this.requirementTypes.filter((t) => t.name !== 'Comment');
      return this.requirementTypes.filter((t) => allowed.includes(t.name));
    },
    descendantCount() {
      const ids = getDescendantIds([this.requirement.id], this.allItems);
      return ids.size - 1;
    },
    childTypeLabel() {
      const t = this.requirementTypes.find((t) => t.id === this.newChildTypeId);
      return t ? t.name.toLowerCase() : 'child';
    },
  },
  watch: {
    requirement(newVal) {
      this.form = { ...newVal, tags: newVal.tags || [] };
      this.showDeleteConfirm = false;
      this.addingChild = false;
      this.loadHistory();
      this.loadRequirementMedia();
      this.autoResizeAll();
    },
  },
  mounted() {
    this.loadHistory();
    this.loadRequirementMedia();
    this.autoResizeAll();
  },
  methods: {
    autoResize(e) {
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    autoResizeAll() {
      this.$nextTick(() => {
        this.$el?.querySelectorAll('textarea').forEach((el) => {
          el.style.height = 'auto';
          el.style.height = el.scrollHeight + 'px';
        });
      });
    },
    async save() {
      if (this.saving) return;
      this.saving = true;
      try {
        const payload = {
          title: this.form.title,
          description: this.form.description,
          status: this.form.status,
          priority: this.form.priority,
          requirement_type: this.form.requirement_type,
          tenant: this.form.tenant,
          owner: this.form.owner,
          parent: this.form.parent || null,
          due_date: this.form.due_date || null,
          tags: this.form.tags,
          metadata: this.form.metadata,
          internal_notes: this.form.internal_notes,
        };
        if (this.form.customer_facing != null) {
          payload.customer_facing = this.form.customer_facing;
        }
        const response = await axios.patch(
          `/api/requirements/${this.form.id}/`,
          payload,
        );
        this.$emit('updated', response.data);
      } catch (err) {
        const data = err.response?.data;
        let msg = 'Failed to save. Please try again.';
        if (data) {
          if (typeof data === 'object' && !Array.isArray(data)) {
            const details = Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join('; ');
            if (details) msg = details;
          } else if (typeof data === 'string') {
            msg = data;
          }
        }
        console.error('Requirement save failed:', err.response?.status, data);
        this.toast.error(msg);
      } finally {
        this.saving = false;
      }
    },
    async onParentChange(newParentId) {
      this.form.parent = newParentId;
      await this.save();
    },
    childTypeBadgeClass(name) {
      return TYPE_CLASSES[name] || 'bg-gray-100 text-gray-600';
    },
    childStatusClass(status) {
      return STATUS_CLASSES[status] || 'bg-gray-100 text-gray-600';
    },
    childStatusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    startAddChild() {
      this.addingChild = true;
      this.newChildTitle = '';
      this.newChildTypeId = this.allowedChildTypes[0]?.id || null;
      this.$nextTick(() => this.$refs.childTitleInput?.focus());
    },
    async submitChild() {
      if (!this.newChildTitle.trim()) return;
      if (!this.newChildTypeId) {
        this.toast.error('Could not resolve child type. Please try again.');
        return;
      }
      try {
        await axios.post('/api/requirements/', {
          title: this.newChildTitle.trim(),
          parent: this.requirement.id,
          tenant: this.requirement.tenant,
          requirement_type: this.newChildTypeId,
          status: 'draft',
          priority: this.requirement.priority || 'none',
        });
        this.newChildTitle = '';
        this.$emit('updated', this.requirement);
      } catch {
        this.toast.error('Failed to create child. Please try again.');
      }
    },
    onTagsChange(newTags) {
      this.form.tags = newTags;
      this.save();
    },
    async loadRequirementMedia() {
      const requirementId = this.requirement?.id;
      if (!requirementId) {
        this.requirementMediaFiles = [];
        return;
      }
      try {
        const { data } = await axios.get('/api/requirement-media/', {
          params: { requirement: requirementId },
        });
        // Do not display the previous ticket's files when a request settles
        // after the user has moved to another Work row.
        if (this.requirement?.id !== requirementId) return;
        this.requirementMediaFiles = Array.isArray(data) ? data : data.results || [];
      } catch {
        if (this.requirement?.id === requirementId) this.requirementMediaFiles = [];
      }
    },
    async removeAttachment(file) {
      const name = file.filename || file.file_name || 'this attachment';
      if (!window.confirm(`Remove ${name} from this ticket? This can't be undone.`)) {
        return;
      }
      try {
        await axios.delete(`/api/requirement-media/${file.id}/`);
        await this.loadRequirementMedia();
        this.toast.success('Attachment removed.');
      } catch {
        this.toast.error('Could not remove that attachment.');
      }
    },
    async onAttachmentUploaded() {
      await this.loadRequirementMedia();
      const uploader = this.$refs.attachUpload;
      if (uploader && !uploader.hasPending()) uploader.clear();
    },
    async deleteAndPromoteChildren() {
      const newParent = this.requirement.parent || null;
      const failed = [];
      for (const child of this.childItems) {
        try {
          await axios.patch(`/api/requirements/${child.id}/`, { parent: newParent });
        } catch {
          failed.push(child.title);
        }
      }
      if (failed.length) {
        this.toast.error(
          `Could not move ${failed.length} ${failed.length === 1 ? 'child' : 'children'}. Delete aborted.`,
        );
        this.$emit('updated', this.requirement);
        return;
      }
      try {
        await axios.delete(`/api/requirements/${this.requirement.id}/`);
        this.showDeleteConfirm = false;
        this.toast.success('Deleted. Children moved to parent.');
        this.$emit('deleted');
      } catch {
        this.toast.error('Children were moved but delete failed. Please try again.');
        this.$emit('updated', this.requirement);
      }
    },
    async deleteWithChildren() {
      try {
        await axios.delete(`/api/requirements/${this.requirement.id}/`);
        this.showDeleteConfirm = false;
        this.toast.success('Deleted successfully.');
        this.$emit('deleted');
      } catch {
        this.toast.error('Failed to delete. Please try again.');
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString();
    },
    onCommentCount(count) {
      this.$emit('comment-count', { id: this.requirement.id, count });
    },
    async loadHistory() {
      if (!this.requirement?.id) {
        this.activities = [];
        return;
      }
      try {
        const { data } = await axios.get(`/api/requirements/${this.requirement.id}/history/`);
        this.activities = data;
      } catch {
        this.activities = [];
      }
    },
    activityIcon(type) {
      const icons = {
        created: ['fas', 'plus'],
        updated: ['fas', 'pencil'],
        status_changed: ['fas', 'arrow-right'],
        assigned: ['fas', 'user'],
        completed: ['fas', 'check'],
        reopened: ['fas', 'rotate-left'],
        linked: ['fas', 'link'],
        unlinked: ['fas', 'link-slash'],
        reordered: ['fas', 'arrows-up-down'],
      };
      return icons[type] || ['fas', 'circle'];
    },
    formatActivity(a) {
      const name = `<span class="font-medium text-gray-900">${this.escapeHtml(a.user_name || 'System')}</span>`;
      const changes = a.field_changes || [];
      switch (a.activity_type) {
        case 'created':
          return `${name} created this`;
        case 'completed':
          return `${name} marked this complete`;
        case 'reopened':
          return `${name} reopened this`;
        case 'reordered':
          return `${name} reordered this`;
        case 'linked':
          return `${name} linked to ${this.escapeHtml(a.metadata?.target_title || 'item')} (${this.escapeHtml(a.metadata?.link_type || 'related')})`;
        case 'unlinked':
          return `${name} removed link to ${this.escapeHtml(a.metadata?.target_title || 'item')}`;
        case 'assigned': {
          const assignee = changes.find(c => c.field === 'owner');
          return assignee
            ? `${name} assigned to ${this.escapeHtml(assignee.new_value || 'Unassigned')}`
            : `${name} changed assignment`;
        }
        case 'status_changed': {
          const sc = changes.find(c => c.field === 'status');
          return sc
            ? `${name} changed status from ${this.escapeHtml(sc.old_value)} to ${this.escapeHtml(sc.new_value)}`
            : `${name} changed status`;
        }
        case 'updated': {
          if (!changes.length) return `${name} updated this`;
          return changes.map(c =>
            `${name} changed ${this.escapeHtml(c.field)} from ${this.escapeHtml(c.old_value || 'empty')} to ${this.escapeHtml(c.new_value || 'empty')}`
          ).join('<br>');
        }
        default:
          return `${name} ${this.escapeHtml(a.activity_type_display || a.activity_type)}`;
      }
    },
    escapeHtml(str) {
      if (!str) return '';
      const s = String(str);
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    },
    formatRelativeTime(dateStr) {
      if (!dateStr) return '';
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return new Date(dateStr).toLocaleDateString();
    },
  },
};
</script>
